import { LocalDate } from "@js-joda/core";
import { SalaryType } from "model/Salary";
import type { UpdateWorkerEmploymentRequest } from "services/api/company/staff.service";
import type {
  SaveEmploymentParams,
  SaveEmploymentResult,
} from "web-apps/company/components/staff/components/profilePanel/profilePanel.contract";
import {
  parseIsoDateToLocalDateOrNull,
  toIsoStartOfDayUtcFromStockholmDate,
} from "web-apps/company/utils/timeAndDate";

export function toLocalDateOrNull(
  value: string | null | undefined,
): LocalDate | null {
  return parseIsoDateToLocalDateOrNull(value);
}

export function toIsoDateStartOfDayUtc(date: LocalDate): string {
  return toIsoStartOfDayUtcFromStockholmDate(date);
}

export function toNonNegativeNumber(value: string): number {
  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) return 0;
  return Math.max(0, parsedValue);
}

export function toNonNegativeIntegerOrZero(value: string): number {
  const trimmedValue = value.trim();
  if (!/^\d+$/.test(trimmedValue)) return 0;
  const parsedValue = Number(trimmedValue);
  if (!Number.isSafeInteger(parsedValue)) return 0;
  return parsedValue;
}

function toEmploymentBasePayload(
  params: SaveEmploymentParams,
): UpdateWorkerEmploymentRequest {
  const form = params.editedEmploymentDetails;

  const workPercentagePercent = toNonNegativeNumber(form.workPercentage);
  const workPercentage =
    form.salaryType === SalaryType.Monthly
      ? Math.min(workPercentagePercent / 100, 1)
      : 0;

  const employmentType = form.employmentType || "Undefined";

  return {
    startDate: toIsoDateStartOfDayUtc(params.effectiveFromDate),
    endDate: params.effectiveEndDate
      ? toIsoDateStartOfDayUtc(params.effectiveEndDate)
      : null,
    employmentType,
    employeeNumber: toNonNegativeIntegerOrZero(form.employeeNumber),
    employeeCategory: form.employeeCategory || "BlueCollar",
    salaryType: form.salaryType || "Undefined",
    workPercentage,
    paymentType: form.paymentType || "None",
    role: form.role.trim(),
    agreement: form.agreement.trim(),
    vacationDays: toNonNegativeIntegerOrZero(form.vacationDays),
    noticeDays: toNonNegativeIntegerOrZero(form.noticeDays),
    workplace: form.workplace.trim(),
  };
}

export function buildUpdateStaffWorkerPayload(
  params: SaveEmploymentParams,
): SaveEmploymentResult {
  return {
    payload: toEmploymentBasePayload(params),
  };
}
